import React, { useState } from 'react';
import styled from 'styled-components';
import MyPageLayout from '../../components/mypage/MyPageLayout';
import CertificateModal from '../../components/modals/CertificateModal';
import { Certificate } from '../../types/certificate';
import { useCertificates } from '../../hooks/useCertificates';

interface MyPageCertificateProps {}

const MyPageCertificate: React.FC<MyPageCertificateProps> = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState<Certificate | null>(null);
  
  const {
    certificates,
    isLoading,
    error,
    currentPage,
    totalPages,
    fetchCertificates
  } = useCertificates({ initialPageSize: 8 });

  // 검색 필터링
  const filteredCertificates = certificates.filter(certificate =>
    certificate.lecture_title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCertificateClick = (certificate: Certificate) => {
    setSelectedCertificate(certificate);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCertificate(null);
  };

  const handlePageChange = (page: number) => {
    fetchCertificates(page);
  };

  return (
    <MyPageLayout hideFooter={true}>
      <Container>
        <MainContainer>
          {/* 페이지 헤더 */}
          <PageHeader>
            <PageTitle>수료증</PageTitle>
          </PageHeader>

          {/* 브레드크럼 */}
          <NavigationBar>
            <BreadcrumbContainer>
              <BreadcrumbItem>마이페이지</BreadcrumbItem>
              <ChevronIcon>
                <svg width="8" height="16" viewBox="0 0 8 16" fill="none">
                  <path 
                    d="M1.73 3.07L6.26 7.6L1.73 12.13" 
                    stroke="rgba(55, 56, 60, 0.61)" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </ChevronIcon>
              <BreadcrumbItem>수료증</BreadcrumbItem>
            </BreadcrumbContainer>
          </NavigationBar>

          {/* 수료증 콘텐츠 */}
          <Section>
            {/* 검색 */}
            <SearchContainer>
              <SearchInput
                placeholder="검색어를 입력해주세요."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <SearchIcon>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M7.333 12.667A5.333 5.333 0 1 0 7.333 2a5.333 5.333 0 0 0 0 10.667ZM14 14l-2.9-2.9" stroke="rgba(55, 56, 60, 0.28)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </SearchIcon>
            </SearchContainer>

            {/* 수료증 컨텐츠 */}
            <ContentSection>
              {isLoading ? (
                <LoadingState>
                  <LoadingSpinner />
                  <LoadingText>수료증을 불러오는 중...</LoadingText>
                </LoadingState>
              ) : error ? (
                <ErrorState>
                  <EmptyIcon>
                    <svg width="160" height="160" viewBox="0 0 24 24" fill="none">
                      <path d="M12 9V11M12 15H12.01M5.07183 19H18.9282C20.4678 19 21.4301 17.3333 20.6603 16L13.7321 4C12.9623 2.66667 11.0377 2.66667 10.2679 4L3.33975 16C2.56995 17.3333 3.53223 19 5.07183 19Z" stroke="#FF6B6B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </EmptyIcon>
                  <EmptyContent>
                    <EmptyTitle>수료증을 불러올 수 없습니다.</EmptyTitle>
                    <EmptySubtitle>{error}</EmptySubtitle>
                    <RetryButton onClick={() => fetchCertificates(0)}>
                      다시 시도
                    </RetryButton>
                  </EmptyContent>
                </ErrorState>
              ) : filteredCertificates.length > 0 ? (
                <>
                  <CertificateGrid>
                    {filteredCertificates.map((certificate) => (
                      <CertificateCard 
                        key={certificate.person_certification_id}
                        onClick={() => handleCertificateClick(certificate)}
                      >
                        <CertificateImage>
                          <img 
                            src={certificate.template_image_url || 'https://placehold.co/205x205'} 
                            alt={certificate.lecture_title}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = 'https://placehold.co/205x205';
                            }}
                          />
                        </CertificateImage>
                        <CertificateInfo>
                          <CertificateTitle>{certificate.lecture_title}</CertificateTitle>
                          <CertificateDate>
                            발급일: {new Date(certificate.issue_at).toLocaleDateString('ko-KR')}
                          </CertificateDate>
                        </CertificateInfo>
                      </CertificateCard>
                    ))}
                  </CertificateGrid>
                  
                  {/* 페이지네이션 */}
                  {totalPages > 1 && (
                    <PaginationContainer>
                      <PaginationButton 
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 0}
                      >
                        이전
                      </PaginationButton>
                      
                      <PageNumbers>
                        {Array.from({ length: totalPages }, (_, index) => (
                          <PageNumber
                            key={index}
                            active={index === currentPage}
                            onClick={() => handlePageChange(index)}
                          >
                            {index + 1}
                          </PageNumber>
                        ))}
                      </PageNumbers>
                      
                      <PaginationButton 
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages - 1}
                      >
                        다음
                      </PaginationButton>
                    </PaginationContainer>
                  )}
                </>
              ) : (
                <EmptyState>
                  <EmptyIcon>
                    <svg width="160" height="160" viewBox="0 0 24 24" fill="none">
                      <path d="M9,21C9,22.1 9.9,23 11,23H13C14.1,23 15,22.1 15,21V20H9V21M12,2C8.14,2 5,5.14 5,9C5,11.38 6.19,13.47 8,14.74V17C8,17.55 8.45,18 9,18H15C15.55,18 16,17.55 16,17V14.74C17.81,13.47 19,11.38 19,9C19,5.14 15.86,2 12,2Z" fill="#171719"/>
                    </svg>
                  </EmptyIcon>
                  <EmptyContent>
                    <EmptyTitle>
                      {searchQuery ? '검색 결과가 없습니다.' : '수료증 발급을 준비 중입니다.'}
                    </EmptyTitle>
                    {searchQuery && (
                      <EmptySubtitle>다른 검색어로 시도해보세요.</EmptySubtitle>
                    )}
                  </EmptyContent>
                </EmptyState>
              )}
            </ContentSection>
          </Section>
        </MainContainer>
      </Container>
      
      {/* 모달 */}
      <CertificateModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        type="certificate"
        title={selectedCertificate?.lecture_title}
        personCertificationId={selectedCertificate?.person_certification_id}
      />
    </MyPageLayout>
  );
};

// 스타일 컴포넌트들
const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
`;

const MainContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 48px;
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 16px;
  margin-top: 60px;
`;

const PageTitle = styled.h1`
  color: #171719;
  font-size: 40px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 52px;
  margin: 0;
`;

const NavigationBar = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const BreadcrumbContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const BreadcrumbItem = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.09px;
`;

const ChevronIcon = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Section = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 24px;
`;

const SearchContainer = styled.div`
  position: relative;
  width: 282px;
  margin-left: auto;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 8px 8px 32px;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.22);
  font-size: 12px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.3px;
  color: rgba(55, 56, 60, 0.28);
  outline: none;
  
  &::placeholder {
    color: rgba(55, 56, 60, 0.28);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ContentSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 48px;
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 160px 0;
  gap: 24px;
`;

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #448181;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.span`
  color: #171719;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
`;

const CertificateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const CertificateCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  cursor: pointer;
`;

const CertificateImage = styled.div`
  width: 100%;
  aspect-ratio: 1;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.08);
  overflow: hidden;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const CertificateInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CertificateTitle = styled.h3`
  color: #171719;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  margin: 0;
`;

const CertificateDate = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20px;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 32px;
`;

const PaginationButton = styled.button`
  padding: 8px 16px;
  border: 1px solid rgba(112, 115, 124, 0.22);
  background: white;
  color: #171719;
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #f5f5f5;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageNumbers = styled.div`
  display: flex;
  gap: 8px;
`;

const PageNumber = styled.button<{ active: boolean }>`
  width: 32px;
  height: 32px;
  border: none;
  background: ${props => props.active ? '#448181' : 'transparent'};
  color: ${props => props.active ? 'white' : '#171719'};
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.active ? '#448181' : '#f5f5f5'};
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 160px 0;
  gap: 24px;
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 160px 0;
  gap: 24px;
`;

const RetryButton = styled.button`
  padding: 12px 24px;
  background: #448181;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 12px;

  &:hover {
    opacity: 0.9;
  }
`;

const EmptyIcon = styled.div`
  width: 160px;
  height: 160px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const EmptyContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const EmptyTitle = styled.h3`
  color: #171719;
  font-size: 20px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 28px;
  text-align: center;
  margin: 0;
`;

const EmptySubtitle = styled.p`
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  text-align: center;
  margin: 0;
`;

export default MyPageCertificate;